import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardList, Code, Shield, Plug, ChevronDown } from 'lucide-react';
import TopBar from '../components/TopBar';
import { ExamplesContent } from './docs/sections/OverviewSections';
import { QuickstartContent, FAQContent, ModelsContent } from './docs/sections/QuickstartSections';
import {
  ApiOverviewContent,
  ApiChatCompletionsContent,
  ApiResponsesContent,
  ApiMessagesContent,
  ApiModelsContent,
  ApiUsageContent,
  ApiStreamingContent,
  ApiErrorsContent,
} from './docs/sections/ApiSections';
import { TermsOfServiceContent, PrivacyPolicyContent } from './docs/sections/PolicySections';
import {
  IdeIntegrationContent,
  CursorGuideContent,
  ClineGuideContent,
  ClaudeCodeGuideContent,
  OpenCodeGuideContent,
  CodexGuideContent,
  CodexIdeGuideContent,
  CodexAppGuideContent,
} from './docs/sections/IdeIntegrationSections';

const navLinkClasses = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors border-l-2 ${isActive
    ? 'border-l-primary bg-primary/8 text-primary font-semibold'
    : 'border-l-transparent text-muted-foreground hover:bg-muted/60 hover:text-foreground'
  }`;

import PageTitle from '../components/PageTitle';

function VSCodeNavGroup({ label, links }: { label: string; links: { path: string; label: string }[] }) {
  const location = useLocation();
  const isChildActive = links.some(({ path }) => location.pathname.endsWith(`/${path}`));
  const [open, setOpen] = useState(isChildActive);

  useEffect(() => {
    if (isChildActive) setOpen(true);
  }, [isChildActive]);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className={`flex w-full items-center justify-between gap-2 rounded-md px-3 py-1.5 text-sm transition-colors border-l-2 ${
          isChildActive && !open
            ? 'border-l-primary bg-primary/8 text-primary font-semibold'
            : 'border-l-transparent text-muted-foreground hover:bg-muted/60 hover:text-foreground'
        }`}
      >
        <span>{label}</span>
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="mt-1 space-y-1 pl-4">
          {links.map(({ path, label: childLabel }) => (
            <NavLink key={path} to={path} className={navLinkClasses} end>
              {childLabel}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function Docs() {
  const { t } = useTranslation('docs');

  const overviewNavLinks = [
    { path: 'overview/quickstart', label: t('sidebar.quickstart') },
    { path: 'overview/examples', label: t('sidebar.code_examples') },
    { path: 'overview/faq', label: t('sidebar.faq') },
  ];

  const apiReferenceNavLinks = [
    { path: 'api/overview', label: t('sidebar.overview') },
    { path: 'api/chat-completions', label: t('sidebar.chat_completions') },
    { path: 'api/responses', label: t('sidebar.responses') },
    { path: 'api/messages', label: t('sidebar.messages') },
    { path: 'api/models', label: t('sidebar.models') },
    { path: 'api/usage', label: t('sidebar.usage') },
    { path: 'api/streaming', label: t('sidebar.streaming') },
    { path: 'api/errors', label: t('sidebar.errors') },
  ];

  const policiesNavLinks = [
    { path: 'policies/terms', label: t('sidebar.terms_of_service') },
    { path: 'policies/privacy', label: t('sidebar.privacy_policy') },
  ];

  const ideTopNavLinks = [
    { path: 'integrations', label: t('sidebar.overview') },
    { path: 'integrations/cursor', label: t('sidebar.cursor') },
  ];

  const vscodeNavLinks = [
    { path: 'integrations/cline', label: t('sidebar.cline') },
    { path: 'integrations/codex-ide', label: t('sidebar.codex_ide') },
  ];

  const ideTailNavLinks = [
    { path: 'integrations/claude-code', label: t('sidebar.claude_code') },
    { path: 'integrations/opencode', label: t('sidebar.opencode') },
    { path: 'integrations/codex', label: t('sidebar.codex') },
    { path: 'integrations/codex-app', label: t('sidebar.codex_app') },
  ];
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <PageTitle 
        titleKey="page_title" 
        namespace="docs" 
        descriptionKey="page_subtitle"
      />
      <TopBar />

      <main className="flex-1 flex">
        {/* Fixed Sidebar */}
        <aside className="hidden md:block w-72 lg:w-80 flex-shrink-0 bg-card border-r border-border overflow-y-auto h-[calc(100vh-4rem)] sticky top-16 sidebar-scroll">
          <div className="p-6 space-y-4">
            <div>
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
                <ClipboardList className="h-3.5 w-3.5" />
                {t('sidebar.overview')}
              </div>
              <nav className="space-y-1">
                {overviewNavLinks.map(({ path, label }) => (
                  <NavLink key={path} to={path} className={navLinkClasses} end>
                    {label}
                  </NavLink>
                ))}
              </nav>
            </div>
            <hr className="border-border/60" />
            <div>
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
                <Code className="h-3.5 w-3.5" />
                {t('sidebar.api_reference')}
              </div>
              <nav className="space-y-1">
                {apiReferenceNavLinks.map(({ path, label }) => (
                  <NavLink key={path} to={path} className={navLinkClasses} end>
                    {label}
                  </NavLink>
                ))}
              </nav>
            </div>
            <hr className="border-border/60" />
            <div>
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
                <Plug className="h-3.5 w-3.5" />
                {t('sidebar.ide_integrations')}
              </div>
              <nav className="space-y-1">
                {ideTopNavLinks.map(({ path, label }) => (
                  <NavLink key={path} to={path} className={navLinkClasses} end>
                    {label}
                  </NavLink>
                ))}
                <VSCodeNavGroup label={t('sidebar.vscode')} links={vscodeNavLinks} />
                {ideTailNavLinks.map(({ path, label }) => (
                  <NavLink key={path} to={path} className={navLinkClasses} end>
                    {label}
                  </NavLink>
                ))}
              </nav>
            </div>
            <hr className="border-border/60" />
            <div>
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
                <Shield className="h-3.5 w-3.5" />
                {t('sidebar.policies')}
              </div>
              <nav className="space-y-1">
                {policiesNavLinks.map(({ path, label }) => (
                  <NavLink key={path} to={path} className={navLinkClasses} end>
                    {label}
                  </NavLink>
                ))}
              </nav>
            </div>
          </div>
        </aside>

        {/* Main Content - Full Width */}
        <section className="flex-1 overflow-y-auto h-[calc(100vh-4rem)]">
          <div className="p-6 md:p-10 lg:p-12 xl:p-16 w-full">
            <Routes>
              <Route index element={<Navigate to="overview/quickstart" replace />} />
              <Route path="overview/quickstart" element={<QuickstartContent />} />
              <Route path="overview/examples" element={<ExamplesContent />} />
              <Route path="overview/faq" element={<FAQContent />} />
              <Route path="api/overview" element={<ApiOverviewContent />} />
              <Route path="api/chat-completions" element={<ApiChatCompletionsContent />} />
              <Route path="api/responses" element={<ApiResponsesContent />} />
              <Route path="api/messages" element={<ApiMessagesContent />} />
              <Route path="api/models" element={<ApiModelsContent />} />
              <Route path="api/usage" element={<ApiUsageContent />} />
              <Route path="api/streaming" element={<ApiStreamingContent />} />
              <Route path="api/errors" element={<ApiErrorsContent />} />
              <Route path="policies/terms" element={<TermsOfServiceContent />} />
              <Route path="policies/privacy" element={<PrivacyPolicyContent />} />
              <Route path="integrations" element={<IdeIntegrationContent />} />
              <Route path="integrations/cursor" element={<CursorGuideContent />} />
              <Route path="integrations/cline" element={<ClineGuideContent />} />
              <Route path="integrations/claude-code" element={<ClaudeCodeGuideContent />} />
              <Route path="integrations/opencode" element={<OpenCodeGuideContent />} />
              <Route path="integrations/codex" element={<CodexGuideContent />} />
              <Route path="integrations/codex-ide" element={<CodexIdeGuideContent />} />
              <Route path="integrations/codex-app" element={<CodexAppGuideContent />} />
              <Route path="*" element={<Navigate to="." replace />} />
            </Routes>
          </div>
        </section>
      </main>
    </div>
  );
}

export default Docs;
